/**
 * SEO Link Normalizer Utility
 * 
 * Ensures all internal links use canonical format:
 * - https://zarooriretail.com (in production)
 * - No www
 * - No trailing slash (except homepage)
 * 
 * Usage:
 * import { normalizeUrl, isCanonicalUrl } from '../utils/seoLinkNormalizer';
 * 
 * <Link to={normalizeUrl('/services/')}>Services</Link>
 */

const DOMAIN = 'zarooriretail.com';
const PROTOCOL = 'https://';

/**
 * Normalize a URL to canonical format
 * @param {string} url - URL to normalize
 * @returns {string} - Canonical URL
 */
export const normalizeUrl = (url) => {
  if (!url) return '/';
  
  // Handle external URLs - return as-is
  if (url.startsWith('http') && !url.includes(DOMAIN)) {
    return url;
  }
  
  // Handle anchor links
  if (url.startsWith('#')) {
    return url;
  }
  
  // Handle mailto and tel links
  if (url.startsWith('mailto:') || url.startsWith('tel:')) {
    return url;
  }
  
  // Extract path from full URL
  let path = url;
  if (url.includes('://')) {
    try {
      const urlObj = new URL(url);
      path = urlObj.pathname + urlObj.search + urlObj.hash;
    } catch {
      path = url.replace(/^https?:\/\/[^/]+/, '');
    }
  }
  
  // Remove trailing slash (except for homepage)
  if (path !== '/' && path.endsWith('/')) {
    path = path.slice(0, -1);
  }
  
  // Ensure starts with /
  if (!path.startsWith('/')) {
    path = '/' + path;
  }
  
  // Remove double slashes
  path = path.replace(/\/+/g, '/');
  
  return path;
};

/**
 * Get full canonical URL (with domain)
 * @param {string} path - URL path
 * @returns {string} - Full canonical URL
 */
export const getCanonicalUrl = (path) => {
  const normalizedPath = normalizeUrl(path);
  return `${PROTOCOL}${DOMAIN}${normalizedPath}`;
};

/**
 * Check if URL is in canonical format
 * @param {string} url - URL to check
 * @returns {boolean} - True if canonical
 */
export const isCanonicalUrl = (url) => {
  if (!url) return false;
  
  // Check for issues
  const issues = [];
  
  if (url.includes('www.')) {
    issues.push('Contains www');
  }
  
  if (url.startsWith('http://')) {
    issues.push('Uses http instead of https');
  }
  
  if (url !== '/' && url.endsWith('/') && !url.includes('?')) {
    issues.push('Has trailing slash');
  }
  
  return issues.length === 0;
};

/**
 * Validate and warn about non-canonical URLs in development
 * @param {string} url - URL to validate
 * @param {string} component - Component name for logging
 */
export const validateLink = (url, component = 'Unknown') => {
  if (process.env.NODE_ENV !== 'development') return;
  
  if (!isCanonicalUrl(url)) {
    console.warn(`[SEO Warning] Non-canonical URL in ${component}: ${url}`);
    console.warn(`[SEO Warning] Normalized: ${normalizeUrl(url)}`);
  }
};

/**
 * List of blocked URL patterns that should never be linked internally
 */
export const BLOCKED_PATTERNS = [
  '/wp-',
  '.php',
  '/admin',
  '/api/',
  '?tab=',
  '?page=',
  '?sort=',
  '?filter=',
  '/index.html',
];

/**
 * Check if URL should be blocked from internal linking
 * @param {string} url - URL to check
 * @returns {boolean} - True if should be blocked
 */
export const shouldBlockLink = (url) => {
  if (!url) return false;
  return BLOCKED_PATTERNS.some(pattern => url.includes(pattern));
};

export default {
  normalizeUrl,
  getCanonicalUrl,
  isCanonicalUrl,
  validateLink,
  shouldBlockLink,
  BLOCKED_PATTERNS,
};
